import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Download, Eye, Trash2, Plus, Calendar, Award, Building2,
  ChevronRight, Clock, Search, Filter, Shield, CheckCircle, XCircle,
  AlertCircle, FileDown, Loader2, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MySspReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ssp-reports`);
      const data = await res.json();
      setReports(data.reports || []);
    } catch (error) {
      console.error('Error fetching SSP reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reportId) => {
    setDeleting(true);
    try {
      await fetch(`${API_URL}/api/ssp-reports/${reportId}`, {
        method: 'DELETE'
      });
      setReports(reports.filter(r => r.reportId !== reportId));
      setShowDeleteModal(false);
      setSelectedReport(null);
    } catch (error) {
      console.error('Error deleting report:', error);
    } finally {
      setDeleting(false);
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 70) return 'text-emerald-400';
    if (percentage >= 40) return 'text-amber-400';
    return 'text-red-400';
  };

  const filteredReports = reports.filter(report => 
    report.assessmentName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    report.organization?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Loading SSP Reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6" data-testid="my-ssp-reports-page">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-violet-400" />
              My SSP Reports
            </h1>
            <p className="text-slate-400 mt-1">
              View and manage your System Security Plan reports
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors"
            data-testid="new-report-button"
          >
            <Plus className="w-5 h-5" />
            Generate New Report
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/20 rounded-lg">
                <FileText className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{reports.length}</div>
                <div className="text-sm text-slate-400">Total Reports</div>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {reports.filter(r => r.complianceScore?.percentage >= 70).length}
                </div>
                <div className="text-sm text-slate-400">Passing (70%+)</div>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {reports.filter(r => r.complianceScore?.percentage < 70 && r.complianceScore?.percentage >= 40).length}
                </div>
                <div className="text-sm text-slate-400">Needs Work</div>
              </div>
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <XCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white">
                  {reports.filter(r => r.complianceScore?.percentage < 40).length}
                </div>
                <div className="text-sm text-slate-400">Critical (Below 40%)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search reports by name or organization..."
            className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
            data-testid="search-reports"
          />
        </div>

        {/* Reports List */}
        {filteredReports.length === 0 ? (
          <div className="text-center py-16 bg-slate-800/30 border border-slate-700 rounded-xl">
            <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No SSP Reports Yet</h3>
            <p className="text-slate-400 mb-6">
              Generate your first SSP Report after completing a HIPAA Assessment
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors"
            >
              Go to Assessments
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredReports.map((report) => (
              <div
                key={report.reportId}
                className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-violet-500/50 transition-colors group"
                data-testid={`report-card-${report.reportId}`}
              >
                <div className="flex items-start justify-between">
                  {/* Left side - Report Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">
                        {report.assessmentName || 'HIPAA Assessment'}
                      </h3>
                      <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded-full border border-violet-500/30">
                        v{report.version}
                      </span>
                      <span className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded-full">
                        {report.framework}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-6 text-sm text-slate-400 mb-4">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {report.organization?.name || 'Organization'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(report.generatedAt).toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(report.generatedAt).toLocaleTimeString()}
                      </span>
                    </div>

                    {/* Assessment Summary */}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-emerald-400">
                        <CheckCircle className="w-4 h-4" />
                        {report.assessmentSummary?.yes || 0} Yes
                      </span>
                      <span className="flex items-center gap-1 text-amber-400">
                        <AlertCircle className="w-4 h-4" />
                        {report.assessmentSummary?.partial || 0} Partial
                      </span>
                      <span className="flex items-center gap-1 text-red-400">
                        <XCircle className="w-4 h-4" />
                        {report.assessmentSummary?.no || 0} No
                      </span>
                      <span className="flex items-center gap-1 text-slate-400">
                        {report.assessmentSummary?.na || 0} N/A
                      </span>
                    </div>
                  </div>

                  {/* Right side - Score & Actions */}
                  <div className="flex items-center gap-6">
                    {/* Compliance Score */}
                    <div className="text-center">
                      <div className={`text-3xl font-bold ${getScoreColor(report.complianceScore?.percentage || 0)}`}>
                        {report.complianceScore?.percentage || 0}%
                      </div>
                      <div className="text-sm text-slate-400">
                        {report.complianceScore?.score || 0} / {report.complianceScore?.maxScore || 0} controls
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/ssp-report?assessmentId=${report.assessmentId}`)}
                        className="p-2 bg-violet-500/20 hover:bg-violet-500/30 text-violet-400 rounded-lg transition-colors"
                        title="View Report"
                        data-testid={`view-report-${report.reportId}`}
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => navigate(`/ssp-report?assessmentId=${report.assessmentId}`)}
                        className="p-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors"
                        title="Download PDF"
                        data-testid={`download-report-${report.reportId}`}
                      >
                        <Download className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedReport(report);
                          setShowDeleteModal(true);
                        }}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete Report"
                        data-testid={`delete-report-${report.reportId}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Report Contents Preview */}
                <div className="mt-4 pt-4 border-t border-slate-700/50">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <FileText className="w-3 h-3" />
                    <span>Includes: {report.reportContents?.join(' • ') || 'Full SSP Report'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowDeleteModal(false)}>
          <div 
            className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Delete SSP Report?</h3>
              <p className="text-slate-400">
                Are you sure you want to delete "{selectedReport.assessmentName} v{selectedReport.version}"? 
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(selectedReport.reportId)}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    Delete Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySspReports;
